import { useState, useMemo, useCallback, useRef } from 'react';
import { Box, makeStyles } from '@material-ui/core';
import { useVirtualizer } from '@tanstack/react-virtual';
import { SBase, SSearchInput, SText } from '@setvi/shared/components';
import {
  QuoteSectionDTO,
  QuoteProductDTO
} from '@setvi/shared/services/react-query';

import { SelectableItemRow } from './selectable-item-row';

const useStyles = makeStyles(() => ({
  content: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflow: 'hidden'
  },
  searchContainer: {
    display: 'flex',
    justifyContent: 'center',
    padding: '16px 32px 24px'
  },
  searchInput: {
    backgroundColor: '#F8F9FA',
    borderRadius: '99px',
    width: '420px',
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: '#AFBAC6',
      borderRadius: '99px'
    },
    '& .MuiOutlinedInput-root': {
      borderRadius: '99px'
    }
  },
  itemsContainer: {
    backgroundColor: '#F8F9FA',
    padding: '16px 32px',
    flex: 1,
    overflow: 'auto',
    position: 'relative',
    minWidth: 0
  },
  rowContainer: {
    backgroundColor: '#FFFFFF',
    padding: '12px',
    borderRadius: '12px',
    width: '100%',
    boxSizing: 'border-box' as const
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '48px 24px',
    backgroundColor: '#FFFFFF',
    borderRadius: '12px',
    gap: '12px'
  },
  emptyStateText: {
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '20px',
    color: '#5D7083',
    textAlign: 'center'
  }
}));

interface MoveItemsToSectionDialogProps {
  section: QuoteSectionDTO;
  availableItems: QuoteProductDTO[];
  onClose: () => void;
  onConfirm: (itemIds: string[]) => void;
}

/**
 * Dialog for moving existing items into a section
 * Shows items that are not in this section and allows multi-selection
 */
export const MoveItemsToSectionDialog = ({
  section,
  availableItems,
  onClose,
  onConfirm
}: MoveItemsToSectionDialogProps) => {
  const classes = useStyles();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedItemIds, setSelectedItemIds] = useState<Set<string>>(
    new Set()
  );

  const sectionName = section.name || 'Unnamed Section';

  // Filter items based on search query
  const filteredItems = useMemo(() => {
    if (!searchQuery.trim()) return availableItems;

    const query = searchQuery.toLowerCase();
    return availableItems.filter(item => {
      const name = (item.name || '').toLowerCase();
      const sku = (item.sku || '').toLowerCase();
      return name.includes(query) || sku.includes(query);
    });
  }, [availableItems, searchQuery]);

  const handleSearchChange = useCallback((value: string) => {
    setSearchQuery(value);
  }, []);

  const handleItemToggle = useCallback((itemId: string) => {
    setSelectedItemIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  }, []);

  const handleConfirm = useCallback((ids: Set<string>) => {
    const itemIds = Array.from(ids);
    onConfirm(itemIds);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getItemId = (item: QuoteProductDTO) => item.id || item._id;

  // Virtualization setup
  const parentRef = useRef<HTMLDivElement>(null);

  const virtualizer = useVirtualizer({
    count: filteredItems.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 96,
    overscan: 5
  });

  return (
    <SBase
      width="45vw"
      minWidth={800}
      height={850}
      disablePadding
      style={{ background: '#FFFFFF' }}
      header={{
        title: 'Move Items to Section',
        description: `Select items from the list to include in "${sectionName}"`,
        onClose
      }}
      footer={{
        onConfirm: () => handleConfirm(selectedItemIds),
        onClose,
        startIcon: null,
        confirmLabel:
          selectedItemIds.size > 0
            ? `Move to Section (${selectedItemIds.size})`
            : 'Move to Section',
        closeLabel: 'Cancel',
        disableConfirm: selectedItemIds.size === 0
      }}>
      <Box className={classes.content}>
        {/* Search */}
        <Box className={classes.searchContainer}>
          <SSearchInput
            placeholder="Search for Products or SKU number"
            onChange={handleSearchChange}
            className={classes.searchInput}
            iconPosition="start"
          />
        </Box>

        {/* Items List */}
        <div className={classes.itemsContainer} ref={parentRef}>
          {filteredItems.length === 0 ? (
            <Box className={classes.emptyState}>
              <SText
                className={classes.emptyStateText}
                title={
                  searchQuery
                    ? 'No items match your search'
                    : 'No items available to move into this section'
                }
              />
              {!searchQuery && (
                <SText
                  className={classes.emptyStateText}
                  title="All existing items are already in sections or there are no items in your quote."
                />
              )}
            </Box>
          ) : (
            <div
              style={{
                height: `${virtualizer.getTotalSize()}px`,
                width: '100%',
                position: 'relative'
              }}>
              {virtualizer.getVirtualItems().map(virtualRow => {
                const item = filteredItems[virtualRow.index];
                const itemId = getItemId(item);
                const isSelected = selectedItemIds.has(itemId);

                return (
                  <div
                    key={itemId}
                    data-index={virtualRow.index}
                    ref={virtualizer.measureElement}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      transform: `translateY(${virtualRow.start}px)`,
                      paddingBottom: '12px'
                    }}>
                    <Box className={classes.rowContainer}>
                      <SelectableItemRow
                        product={item}
                        searchQuery={searchQuery}
                        selected={isSelected}
                        onToggle={() => handleItemToggle(itemId)}
                      />
                    </Box>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </Box>
    </SBase>
  );
};
